import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { usePostHog } from '@posthog/react';
import instance from '../api/api';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BLU   = '#1e40af';
const BLU2  = '#1d4ed8';
const BLU_L = '#eff6ff';
const FONT  = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

const INPUT_BASE = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 13px', fontSize: 14, color: '#111827',
  border: '1.5px solid #e2e8f0', borderRadius: 8,
  background: '#fff', outline: 'none', fontFamily: FONT,
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const LABEL_BASE = {
  display: 'block', fontSize: 12, fontWeight: 700,
  color: '#374151', marginBottom: 5, letterSpacing: '0.1px',
};
const BTN_PRIMARY = {
  padding: '10px 24px', background: BLU, color: '#fff',
  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: FONT,
};
const BTN_SECONDARY = {
  padding: '10px 20px', background: '#fff', color: '#374151',
  border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: FONT,
};

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_COLORS = {
  caribbeanCurrent: '#2A5CAA', riskRed: '#F55858',
  riskYellow: 'rgba(251, 191, 76, 1)', riskGreen: 'rgba(0, 213, 120, 1)',
  adminBlue: '#2F5A74', lightRed: '#F3A5A5', orange: '#F97502',
  tangerine: '#F28F3B', red: '#EA0B0B', yellow: 'rgba(251, 191, 76, 1)',
  green: 'rgba(0, 213, 120, 1)', dirtyGreen: '#CCDE0E', brightGreen: '#45BC37',
  jetBlack: '#353535', lemon: '#CAB500', whiteSmoke: '#F5F5F5',
  jet: 'rgba(53, 53, 53, 1)', inactiveEmployee: 'rgba(47,90,116,0.25)',
  inactiveAdmin: 'rgba(242,143,59,0.5)',
};

const DEFAULT_SCALES = {
  Impact:     { 1: 'Insignificant', 2: 'Minor',             3: 'Moderate',    4: 'Major',  5: 'Severe'         },
  Likelihood: { 1: 'Rare',          2: 'Unlikely',          3: 'Possible',    4: 'Likely', 5: 'Almost Certain' },
  Control:    { 1: 'Weak',          2: 'Fair',              3: 'Satisfactory',4: 'Good',   5: 'Excellent'      },
  Acceptance: { 1: 'Unacceptable',  2: 'Needs Improvement', 3: 'Acceptable',  4: 'Good',   5: 'Excellent'      },
};

const STEPS = ['Company Info', 'Branding', 'Risk Config', 'Admin Account', 'Review'];

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function useFocusProps() {
  return {
    onFocus: e => { e.target.style.borderColor = BLU; e.target.style.boxShadow = `0 0 0 3px ${BLU_L}`; },
    onBlur:  e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; },
  };
}

function Field({ label, hint, required, tooltip, children }) {
  return (
    <div>
      <label style={LABEL_BASE}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        {tooltip && <InfoTip text={tooltip} />}
      </label>
      {children}
      {hint && <p style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 5, lineHeight: 1.5 }}>{hint}</p>}
    </div>
  );
}

function InfoTip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 6, verticalAlign: 'middle' }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{
          width: 15, height: 15, borderRadius: '50%',
          background: show ? BLU : '#e5e7eb', color: show ? '#fff' : '#6b7280',
          fontSize: 9, fontWeight: 800, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', cursor: 'default',
          transition: 'background 0.15s, color 0.15s', userSelect: 'none',
        }}
      >?</span>
      {show && (
        <span style={{
          position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
          zIndex: 99, width: 240, background: '#111827', color: '#f9fafb',
          fontSize: 12, borderRadius: 8, padding: '8px 12px', lineHeight: 1.5,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', pointerEvents: 'none',
          fontWeight: 400,
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', whiteSpace: 'nowrap' }}>{title}</span>
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
    </div>
  );
}

function IconBtn({ onClick, danger, children, title }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        padding: '0 10px', height: 40, border: '1.5px solid',
        borderColor: h ? (danger ? '#fca5a5' : '#bfdbfe') : '#e2e8f0',
        borderRadius: 8, background: h ? (danger ? '#fef2f2' : BLU_L) : '#fff',
        color: h ? (danger ? '#ef4444' : BLU) : '#94a3b8',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'all 0.15s',
      }}
    >{children}</button>
  );
}

// ─── Key Gate ─────────────────────────────────────────────────────────────────

function KeyGate({ value, onChange, onSubmit }) {
  const fp = useFocusProps();
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: BLU, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', boxShadow: `0 6px 20px ${BLU}40` }}>
            <img src="/rsmLogo.png" alt="RSM" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.4px' }}>Tenant Onboarding</h1>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Enter your platform key to continue</p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '28px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <Field label="Platform Key" required hint="Internal use only — do not share with clients">
            <input
              type="password"
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSubmit()}
              placeholder="••••••••"
              style={INPUT_BASE}
              {...fp}
            />
          </Field>
          <button
            onClick={onSubmit}
            style={{ ...BTN_PRIMARY, width: '100%', marginTop: 16 }}
            onMouseOver={e => e.currentTarget.style.background = BLU2}
            onMouseOut={e => e.currentTarget.style.background = BLU}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Horizontal step indicator ────────────────────────────────────────────────

function StepBar({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STEPS.map((label, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done || active ? BLU : '#e5e7eb',
                color: done || active ? '#fff' : '#9ca3af',
                fontSize: 11, fontWeight: 800,
                boxShadow: active ? `0 0 0 4px ${BLU_L}` : 'none',
                transition: 'all 0.2s',
              }}>
                {done
                  ? <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  : i + 1
                }
              </div>
              <span style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? BLU : done ? '#374151' : '#9ca3af', whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: i < current ? BLU : '#e5e7eb', margin: '0 10px', minWidth: 20, borderRadius: 2, transition: 'background 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Company Info ─────────────────────────────────────────────────────

function CompanyInfoStep({ companyName, setCompanyName, domains, addDomain, removeDomain, updateDomain, logoUrl, setLogoUrl }) {
  const fp = useFocusProps();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Field label="Company Name" required tooltip="The full legal or trading name of the organisation. Appears in headings, emails, and the sidebar across the platform.">
        <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Corp" style={INPUT_BASE} {...fp} />
      </Field>

      <Field label="Domain(s)" required hint="Hostname(s) the client's staff will visit — no https:// or trailing slash (e.g. acmecorp.com)" tooltip="Used to look up this tenant's config on every page load. Must match what's added to STATIC_CONFIG_MAP in configLoader.js.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {domains.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 8 }}>
              <input value={d} onChange={e => updateDomain(i, e.target.value)} placeholder="acmecorp.com" style={INPUT_BASE} {...fp} />
              {domains.length > 1 && (
                <IconBtn onClick={() => removeDomain(i)} danger title="Remove domain">
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </IconBtn>
              )}
            </div>
          ))}
        </div>
        <button onClick={addDomain} style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: BLU, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT }}>
          + Add another domain
        </button>
      </Field>

      <Field label="Logo URL" hint="Relative path in the public/ folder (e.g. /acmeLogo.png). Add the file to public/ before deploying." tooltip="Used in the sidebar and topbar. Supported: PNG, SVG, WebP. Leave blank to show initials instead.">
        <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="/acmeLogo.png" style={INPUT_BASE} {...fp} />
      </Field>
    </div>
  );
}

// ─── Step 2: Branding ─────────────────────────────────────────────────────────

function BrandingStep({ primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor, companyName, logoUrl }) {
  const fp = useFocusProps();
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
        {[
          { label: 'Primary Color', value: primaryColor, set: setPrimaryColor, hint: 'Sidebar background, primary buttons, and headings', tip: "The client's main brand colour." },
          { label: 'Secondary Color', value: secondaryColor, set: setSecondaryColor, hint: 'Badges, accents, and secondary buttons', tip: "An accent colour for highlights and role badges." },
        ].map(({ label, value, set, hint, tip }) => (
          <Field key={label} label={label} hint={hint} tooltip={tip}>
            <div style={{ display: 'flex', gap: 10 }}>
              <input type="color" value={value} onChange={e => set(e.target.value)} style={{ width: 44, height: 44, borderRadius: 8, cursor: 'pointer', border: '1.5px solid #e2e8f0', padding: 3, background: '#fff', flexShrink: 0 }} />
              <input value={value} onChange={e => set(e.target.value)} style={{ ...INPUT_BASE, fontFamily: 'monospace', fontSize: 13 }} {...fp} />
            </div>
          </Field>
        ))}
      </div>

      <SectionHeader title="Live preview" />
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {logoUrl ? <img src={logoUrl} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} onError={() => {}} /> : <div style={{ width: 22, height: 22, borderRadius: 6, background: primaryColor }} />}
            <span style={{ fontSize: 14, fontWeight: 700, color: primaryColor }}>{companyName || 'Company Name'}</span>
          </div>
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 100, background: secondaryColor, color: '#fff', fontWeight: 700 }}>Admin</span>
        </div>
        <div style={{ display: 'flex', height: 120 }}>
          <div style={{ width: 150, background: primaryColor, padding: '10px 0' }}>
            {['Risk Register', 'Assessment', 'Response'].map((item, i) => (
              <div key={item} style={{ padding: '7px 14px', fontSize: 12, color: i === 0 ? '#fff' : 'rgba(255,255,255,0.45)', fontWeight: i === 0 ? 700 : 400, background: i === 0 ? 'rgba(255,255,255,0.15)' : 'transparent', borderRadius: i === 0 ? '0 6px 6px 0' : 0, marginRight: i === 0 ? 6 : 0 }}>
                {item}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, background: '#f8fafc', padding: 16 }}>
            <div style={{ height: 10, background: '#e2e8f0', borderRadius: 3, width: '40%', marginBottom: 10 }} />
            <div style={{ height: 7, background: '#f1f5f9', borderRadius: 3, width: '60%', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ padding: '6px 14px', borderRadius: 7, background: primaryColor, color: '#fff', fontSize: 12, fontWeight: 700 }}>Promote</div>
              <div style={{ padding: '6px 14px', borderRadius: 7, background: secondaryColor, color: '#fff', fontSize: 12, fontWeight: 700 }}>Export</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Risk Config ──────────────────────────────────────────────────────

function RiskConfigStep({ perspectives, addPerspective, removePerspective, updatePerspective, getFactors, addFactor, removeFactor, updateFactor, scales, updateScale }) {
  const fp = useFocusProps();
  return (
    <div>
      <SectionHeader title="Perspectives & Strategic Objectives" />
      <p style={{ fontSize: 13, color: '#6b7280', margin: '-4px 0 16px', lineHeight: 1.6 }}>
        Perspectives are top-level risk categories (e.g. Financial, Operational). Each groups Strategic Objectives — specific goals that risks are mapped against.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
        {perspectives.map((p, pi) => (
          <div key={pi} style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 8, padding: 12, background: '#f8fafc', borderBottom: p ? '1px solid #e2e8f0' : 'none' }}>
              <input value={p} onChange={e => updatePerspective(pi, e.target.value)} placeholder={`Perspective ${pi + 1}  (e.g. Financial)`} style={{ ...INPUT_BASE, fontWeight: 600, flex: 1 }} {...fp} />
              {perspectives.length > 1 && (
                <IconBtn onClick={() => removePerspective(pi)} danger title="Remove perspective">
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </IconBtn>
              )}
            </div>
            {p && (
              <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {getFactors(p).map((f, fi) => (
                  <div key={fi} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: '#d1d5db', fontSize: 16, flexShrink: 0, lineHeight: 1 }}>—</span>
                    <input value={f} onChange={e => updateFactor(p, fi, e.target.value)} placeholder="Strategic objective" style={INPUT_BASE} {...fp} />
                    {getFactors(p).length > 1 && (
                      <IconBtn onClick={() => removeFactor(p, fi)} danger title="Remove objective">
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </IconBtn>
                    )}
                  </div>
                ))}
                <button onClick={() => addFactor(p)} style={{ fontSize: 12, fontWeight: 700, color: BLU, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', paddingLeft: 28, fontFamily: FONT }}>
                  + Add objective
                </button>
              </div>
            )}
          </div>
        ))}
        <button onClick={addPerspective} style={{ fontSize: 12, fontWeight: 700, color: BLU, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, padding: 0, textAlign: 'left' }}>
          + Add perspective
        </button>
      </div>

      <SectionHeader title="Scoring Scale Labels" />
      <p style={{ fontSize: 13, color: '#6b7280', margin: '-4px 0 16px', lineHeight: 1.6 }}>
        Default labels work for most clients. Edit only if they use different terminology.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {Object.keys(scales).map(scaleName => (
          <div key={scaleName} style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{scaleName}</span>
            </div>
            <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[1, 2, 3, 4, 5].map(level => (
                <div key={level} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: '#9ca3af', width: 14, textAlign: 'right', flexShrink: 0, fontFamily: 'monospace' }}>{level}</span>
                  <input value={scales[scaleName][level]} onChange={e => updateScale(scaleName, level, e.target.value)} style={{ ...INPUT_BASE, padding: '7px 10px', fontSize: 13 }} {...fp} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 4: Admin Account ────────────────────────────────────────────────────

function AdminAccountStep({ firstName, setFirstName, lastName, setLastName, email, setEmail, password, setPassword, departmentName, setDepartmentName }) {
  const fp = useFocusProps();
  const [showPass, setShowPass] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 4px', lineHeight: 1.6 }}>
        This user becomes the client's main administrator. They can create all other users and departments after logging in.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="First Name" required>
          <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" style={INPUT_BASE} {...fp} />
        </Field>
        <Field label="Last Name" required>
          <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Smith" style={INPUT_BASE} {...fp} />
        </Field>
      </div>

      <Field label="Email Address" required hint="Must be unique across the platform. Avoid shared inboxes — password reset emails go here." tooltip="The admin will use this to log in and receive notifications.">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john.smith@acmecorp.com" style={INPUT_BASE} {...fp} />
      </Field>

      <Field label="Temporary Password" required hint="Share securely and ask the admin to change it on first login." tooltip="Min. 8 characters. Mix of letters, numbers, and symbols recommended.">
        <div style={{ position: 'relative' }}>
          <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" style={{ ...INPUT_BASE, paddingRight: 44 }} {...fp} />
          <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center', padding: 0 }}>
            {showPass
              ? <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
              : <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            }
          </button>
        </div>
      </Field>

      <Field label="First Department Name" required hint="The admin can add more departments inside the app." tooltip="Every user must belong to a department. This creates the first one.">
        <input value={departmentName} onChange={e => setDepartmentName(e.target.value)} placeholder="Administration" style={INPUT_BASE} {...fp} />
      </Field>
    </div>
  );
}

// ─── Step 5: Review ───────────────────────────────────────────────────────────

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 14, padding: '9px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 130, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#111827', wordBreak: 'break-all', lineHeight: 1.5 }}>{value}</span>
    </div>
  );
}

function ReviewSection({ title, children }) {
  return (
    <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ padding: '9px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{title}</span>
      </div>
      <div style={{ padding: '0 16px' }}>{children}</div>
    </div>
  );
}

function ReviewStep({ payload }) {
  const Missing = () => <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 12 }}>Missing — go back and fill this in</span>;
  return (
    <div>
      <ReviewSection title="Company">
        <ReviewRow label="Name" value={payload.companyName || <Missing />} />
        <ReviewRow label="Domain(s)" value={payload.domains.length ? payload.domains.join(', ') : <Missing />} />
        <ReviewRow label="Logo URL" value={payload.branding.logoUrl || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None set</span>} />
      </ReviewSection>

      <ReviewSection title="Branding">
        <div style={{ padding: '12px 0', display: 'flex', gap: 20 }}>
          {[['Primary', payload.branding.primaryColor], ['Secondary', payload.branding.secondaryColor]].map(([label, color]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: color, border: '2px solid #e2e8f0' }} />
              <span style={{ fontSize: 13, color: '#374151' }}>{label}: <code style={{ fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>{color}</code></span>
            </div>
          ))}
        </div>
      </ReviewSection>

      <ReviewSection title="Risk Config">
        <ReviewRow label="Perspectives" value={payload.riskConfig.Perspective.filter(Boolean).join(', ') || <Missing />} />
        <ReviewRow label="Total objectives" value={payload.riskConfig.Factors.length} />
        {Object.keys(DEFAULT_SCALES).map(s => (
          <ReviewRow key={s} label={`${s} scale`} value={Object.values(payload.riskConfig[s]).join(' → ')} />
        ))}
      </ReviewSection>

      <ReviewSection title="First Admin">
        <ReviewRow label="Name" value={`${payload.firstAdmin.firstName} ${payload.firstAdmin.lastName}`.trim() || <Missing />} />
        <ReviewRow label="Email" value={payload.firstAdmin.email || <Missing />} />
        <ReviewRow label="Password" value={payload.firstAdmin.password ? '••••••••' : <Missing />} />
        <ReviewRow label="Department" value={payload.firstAdmin.departmentName || <Missing />} />
      </ReviewSection>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '13px 16px', fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
        <strong>After submitting:</strong> The config file will download automatically. Move it to <code style={{ background: '#fef3c7', padding: '0 4px', borderRadius: 4, fontSize: 12 }}>src/configs/</code> and add the domain mapping to <code style={{ background: '#fef3c7', padding: '0 4px', borderRadius: 4, fontSize: 12 }}>configLoader.js</code>.
      </div>
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────

function SuccessScreen({ companyName, adminEmail, configName, domains }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 60, height: 60, borderRadius: '50%', background: BLU, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: `0 8px 24px ${BLU}40` }}>
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.4px' }}>Tenant Created</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
            <strong style={{ color: '#374151' }}>{companyName}</strong> is now live on the backend.
          </p>
        </div>

        <div style={{ background: BLU_L, border: '1px solid #bfdbfe', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: BLU, margin: '0 0 8px' }}>
            Config file downloaded: <code style={{ background: '#dbeafe', padding: '1px 7px', borderRadius: 5, fontSize: 12, fontFamily: 'monospace' }}>{configName}.json</code>
          </p>
          <p style={{ fontSize: 13, color: '#3b82f6', margin: '0 0 10px', lineHeight: 1.5 }}>
            Move to <code style={{ background: '#dbeafe', padding: '0 5px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>src/configs/</code>, then add to <code style={{ background: '#dbeafe', padding: '0 5px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>STATIC_CONFIG_MAP</code>:
          </p>
          <pre style={{ background: '#dbeafe', borderRadius: 8, padding: '10px 14px', margin: 0, fontSize: 12, color: BLU, fontFamily: 'monospace', overflowX: 'auto' }}>
            {domains.map(d => `'${d}': '${configName}',`).join('\n')}
          </pre>
        </div>

        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>What to do next</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              `Move ${configName}.json → src/configs/ and add the domain mapping`,
              'Push to develop and check branding looks right on staging',
              `Share the temporary password with ${adminEmail} securely`,
              'Ask the admin to log in, change their password, and add remaining users',
              'Merge to master when ready to go live',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: BLU, color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => window.location.reload()}
          style={{ ...BTN_SECONDARY, width: '100%', textAlign: 'center' }}
          onMouseOver={e => e.currentTarget.style.borderColor = '#cbd5e1'}
          onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        >
          Onboard another tenant
        </button>
      </div>
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function TenantOnboarding() {
  const posthog = usePostHog();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [platformKey, setPlatformKey] = useState('');
  const [keyVerified, setKeyVerified] = useState(false);
  const [configName, setConfigName] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [domains, setDomains] = useState(['']);
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3C6E71');
  const [secondaryColor, setSecondaryColor] = useState('#FF8552');
  const [perspectives, setPerspectives] = useState(['']);
  const [factors, setFactors] = useState({});
  const [scales, setScales] = useState(DEFAULT_SCALES);
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [departmentName, setDepartmentName] = useState('');

  useEffect(() => { posthog.capture('superadmin_onboarding_viewed'); }, [posthog]);
  useEffect(() => {
    if (keyVerified) posthog.capture('tenant_onboarding_step_viewed', { step_index: step, step_name: STEPS[step] });
  }, [step, keyVerified, posthog]);

  function verifyKey() {
    if (!platformKey.trim()) { toast.error('Please enter the platform key'); return; }
    const envKey = process.env.REACT_APP_SUPERADMIN_KEY;
    if (envKey && platformKey !== envKey) {
      posthog.capture('superadmin_key_rejected');
      toast.error('Invalid platform key');
      return;
    }
    posthog.capture('superadmin_key_verified');
    setKeyVerified(true);
  }

  function goToStep(next) {
    posthog.capture('tenant_onboarding_step_changed', { from_step: step, to_step: next, direction: next > step ? 'forward' : 'back' });
    setStep(next);
  }

  const addDomain    = () => setDomains([...domains, '']);
  const removeDomain = i => setDomains(domains.filter((_, j) => j !== i));
  const updateDomain = (i, v) => { const n = [...domains]; n[i] = v; setDomains(n); };

  const addPerspective    = () => setPerspectives([...perspectives, '']);
  const removePerspective = i => {
    const name = perspectives[i];
    setPerspectives(perspectives.filter((_, j) => j !== i));
    const nf = { ...factors }; delete nf[name]; setFactors(nf);
  };
  const updatePerspective = (i, val) => {
    const old = perspectives[i]; const next = [...perspectives]; next[i] = val; setPerspectives(next);
    if (old && factors[old] !== undefined) { const nf = { ...factors, [val]: factors[old] }; delete nf[old]; setFactors(nf); }
  };
  const getFactors   = p => factors[p] || [''];
  const addFactor    = p => setFactors({ ...factors, [p]: [...getFactors(p), ''] });
  const removeFactor = (p, i) => setFactors({ ...factors, [p]: getFactors(p).filter((_, j) => j !== i) });
  const updateFactor = (p, i, v) => { const n = [...getFactors(p)]; n[i] = v; setFactors({ ...factors, [p]: n }); };
  const updateScale  = (name, level, v) => setScales({ ...scales, [name]: { ...scales[name], [level]: v } });

  function buildPayload() {
    const factorsArray = [];
    perspectives.forEach(p => { if (!p) return; getFactors(p).forEach(f => { if (f) factorsArray.push({ [p]: f }); }); });
    return {
      companyName, domains: domains.filter(Boolean),
      branding: { logoUrl: logoUrl || null, primaryColor, secondaryColor },
      riskConfig: { Perspective: perspectives.filter(Boolean), Factors: factorsArray, ...scales },
    };
  }

  function downloadConfigFile(p) {
    const name = p.companyName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const content = {
      companyName: p.companyName, domain: p.domains[0],
      branding: { logoUrl: p.branding.logoUrl, faviconUrl: '/favicon.ico', primaryColor: p.branding.primaryColor, secondaryColor: p.branding.secondaryColor, colors: DEFAULT_COLORS },
      config: { Perspective: p.riskConfig.Perspective, Factors: p.riskConfig.Factors, Impact: scales.Impact, Likelihood: scales.Likelihood, Control: scales.Control, Acceptance: scales.Acceptance },
    };
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' }));
    a.download = `${name}.json`;
    a.click();
    return name;
  }

  async function handleSubmit() {
    const p = buildPayload();
    if (!p.companyName || !p.domains.length || !adminEmail || !adminPassword || !departmentName) {
      toast.error('Please fill in all required fields before submitting');
      return;
    }
    const fullPayload = { ...p, firstAdmin: { firstName: adminFirstName, lastName: adminLastName, email: adminEmail, password: adminPassword, departmentName } };
    posthog.capture('tenant_onboarding_submit_attempted', { company_name: p.companyName, domain_count: p.domains.length });
    setSubmitting(true);
    try {
      await instance.post('/api/superadmin/tenants', fullPayload, { headers: { 'X-Superadmin-Key': platformKey } });
      const name = downloadConfigFile(p);
      setConfigName(name);
      setDone(true);
      posthog.capture('tenant_onboarding_completed', { company_name: p.companyName, domains: p.domains });
      toast.success('Tenant created successfully!');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create tenant';
      posthog.capture('tenant_onboarding_failed', { company_name: p.companyName, error: msg, status_code: err?.response?.status });
      toast.error(msg);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  if (!keyVerified) return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <KeyGate value={platformKey} onChange={setPlatformKey} onSubmit={verifyKey} />
    </>
  );

  if (done) return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <SuccessScreen companyName={companyName} adminEmail={adminEmail} configName={configName} domains={domains.filter(Boolean)} />
    </>
  );

  const payload = buildPayload();
  const reviewPayload = { ...payload, firstAdmin: { firstName: adminFirstName, lastName: adminLastName, email: adminEmail, password: adminPassword, departmentName } };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: FONT }}>
      <ToastContainer position="top-right" autoClose={4000} />

      {/* Top header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 40px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: BLU, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img src="/rsmLogo.png" alt="RSM" style={{ width: 24, height: 24, objectFit: 'contain' }} />
          </div>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Tenant Onboarding</span>
        </div>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>Step {step + 1} of {STEPS.length}</span>
      </div>

      {/* Step bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '16px 40px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <StepBar current={step} />
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 40px 100px' }}>
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: '36px 40px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 24 }}>
          {step === 0 && <CompanyInfoStep companyName={companyName} setCompanyName={setCompanyName} domains={domains} addDomain={addDomain} removeDomain={removeDomain} updateDomain={updateDomain} logoUrl={logoUrl} setLogoUrl={setLogoUrl} />}
          {step === 1 && <BrandingStep primaryColor={primaryColor} setPrimaryColor={setPrimaryColor} secondaryColor={secondaryColor} setSecondaryColor={setSecondaryColor} companyName={companyName} logoUrl={logoUrl} />}
          {step === 2 && <RiskConfigStep perspectives={perspectives} addPerspective={addPerspective} removePerspective={removePerspective} updatePerspective={updatePerspective} getFactors={getFactors} addFactor={addFactor} removeFactor={removeFactor} updateFactor={updateFactor} scales={scales} updateScale={updateScale} />}
          {step === 3 && <AdminAccountStep firstName={adminFirstName} setFirstName={setAdminFirstName} lastName={adminLastName} setLastName={setAdminLastName} email={adminEmail} setEmail={setAdminEmail} password={adminPassword} setPassword={setAdminPassword} departmentName={departmentName} setDepartmentName={setDepartmentName} />}
          {step === 4 && <ReviewStep payload={reviewPayload} />}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {step > 0
            ? <button onClick={() => goToStep(step - 1)} style={BTN_SECONDARY} onMouseOver={e => e.currentTarget.style.borderColor = '#cbd5e1'} onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}>← Back</button>
            : <div />
          }
          {step < STEPS.length - 1
            ? <button onClick={() => goToStep(step + 1)} style={BTN_PRIMARY} onMouseOver={e => e.currentTarget.style.background = BLU2} onMouseOut={e => e.currentTarget.style.background = BLU}>Next →</button>
            : <button onClick={handleSubmit} disabled={submitting} style={{ ...BTN_PRIMARY, background: submitting ? '#93c5fd' : BLU, cursor: submitting ? 'not-allowed' : 'pointer' }} onMouseOver={e => { if (!submitting) e.currentTarget.style.background = BLU2; }} onMouseOut={e => { if (!submitting) e.currentTarget.style.background = BLU; }}>
                {submitting ? 'Creating tenant…' : 'Create Tenant'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
